/**
 * Generic export/import hooks for any module.
 * Each module's export/import API follows the same pattern:
 *   GET  /api/v1/{module}/export-import/export   → JSON blob
 *   POST /api/v1/{module}/export-import/validate  → validation result
 *   POST /api/v1/{module}/export-import/import    → import result
 */

import { useMutation } from '@tanstack/react-query';
import { API_BASE_URL } from '../api/config';
import { AUTH_STORAGE_KEYS } from '../auth/storageKeys';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface ExportImportValidateResult {
  valid: boolean;
  errors: string[] | null;
  metadata: {
    entity_counts: Record<string, number>;
  } | null;
  exported_at: string | null;
  source_tenant_id: string | null;
}

export interface ExportImportResult {
  success: boolean;
  imported_counts: Record<string, number>;
  total_imported: number;
  id_map_size: number;
  tags_applied?: number;
  errors: Array<{
    entity_type: string;
    error: string;
  }>;
}

export interface ImportArgs {
  file: File;
  /** Tag ids to attach to every imported taggable entity (optional). */
  tagIds?: string[];
}

export interface ExportParams {
  [key: string]: string[];
}

/**
 * Hook to export module data as a downloadable JSON blob.
 * Supports optional query params for selective export.
 *
 * `subPath` selects the export variant (default `'export'`); pass
 * `'export/flat'` for a module that offers a flat/tabular export.
 */
export function useModuleExport(moduleName: string, subPath: string = 'export') {
  const endpoint = `/api/v1/${moduleName}/export-import`;

  return useMutation({
    mutationFn: async (params?: ExportParams): Promise<Blob> => {
      const url = new URL(`${API_BASE_URL}${endpoint}/${subPath}`, window.location.origin);
      if (params) {
        for (const [key, values] of Object.entries(params)) {
          for (const value of values) {
            url.searchParams.append(key, value);
          }
        }
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      return response.blob();
    },
  });
}

/**
 * Hook to validate an import file before actually importing.
 */
export function useModuleValidateImport(moduleName: string) {
  const endpoint = `/api/v1/${moduleName}/export-import`;

  return useMutation({
    mutationFn: async (file: File): Promise<ExportImportValidateResult> => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}${endpoint}/validate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      return response.json();
    },
  });
}

/**
 * Hook to import module data from a JSON file.
 */
export function useModuleImport(moduleName: string) {
  const endpoint = `/api/v1/${moduleName}/export-import`;

  return useMutation({
    mutationFn: async ({ file, tagIds }: ImportArgs): Promise<ExportImportResult> => {
      const formData = new FormData();
      formData.append('file', file);
      for (const tagId of tagIds ?? []) {
        formData.append('tag_ids', tagId);
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw error;
      }

      const result = await response.json();
      return result.data;
    },
  });
}
